import { getTextLibraryGroups, getTextLibraryLanguages, searchTextLibrary } from "../../../../api/textLibrary";
import { WebEditorContext, TextLibraryContext, MetaforceContext } from "../../../../contexts";
import { useContext, useEffect, useState, useCallback, useRef } from "react";
import * as Constants from "../../../../utils/constants"
import { SidebarTextInput } from "../../../../components/Form/SideBar/SideBarTextInput";
import { SidebarSelect } from "../../../../components/Form/SideBar/SideBarSelect";
import { SidebarDate } from "../../../../components/Form/SideBar/SideBarDate";
import { Formik } from "formik";
import * as yup from "yup";
import _ from "lodash";
import { useToastAction } from "../../../../hooks/useToastAction";
import Pagination from "../../../../components/Pagination";
import { Transition } from "@headlessui/react";
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../../../translations/translationsConstants";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import GenericModal from "../../../../components/Modals/genericModal";
import RichTextEditor from "../RichTextEditor"
import { useQuill } from "../../../../hooks/useQuill";
import Quill from "../RichTextEditor/quilljsEditor";

export default function TextLibraryComponent() {
    const { startParameter } = useContext(MetaforceContext);
    const [activeTemplateText, setActiveTemplateText] = useState();
    const { editorDisabled, lastUsedQuillIndexRef, searchClickedCount, lastUsedQuillRef, editorFileRef, isLoading, autoSaveEditorFile } = useContext(WebEditorContext);
    const { selectedEditorInsertPoint, setSelectedEditorInsertPoint } = useContext(TextLibraryContext);
    const { t: te } = useTranslation([resources.edit]);
    const { t: terr } = useTranslation([resources.errors]);
    const [groupOptions, setGroupOptions] = useState([]);
    const [languageOptions, setLanguageOptions] = useState([]);
    const [showCopyText, setShowCopyText] = useState(null);
    const searchInputRef = useRef();
    const searchResultItemsRef = useRef([]);
    const copyTextRef = useRef();
    const { getAndCheckIndex, insideEmptyParagraph, atStartOfParagraph, atEndOfParagraph } = useQuill();

    const sortPropertyOptions = [
        { name: te(translations.edit.library.name), value: 0 },
        { name: te(translations.edit.library.group), value: 1 },
        { name: te(translations.edit.library.language), value: 2 },
        { name: te(translations.edit.library.validFrom), value: 3 },
        { name: te(translations.edit.library.validTo), value: 4 },
        { name: te(translations.edit.library.createdTime), value: 5 },
        { name: te(translations.edit.library.createdByUserName), value: 6 },
        { name: te(translations.edit.library.modifiedTime), value: 7 },
        { name: te(translations.edit.library.modifiedByUserName), value: 8 },
    ];

    const sortDirectionOptions = [
        { name: te(translations.edit.library.ascending), value: 0 },
        { name: te(translations.edit.library.descending), value: 1 },
    ]
    const [searchRequest, setSearchRequest] = useState({
        startParameter: startParameter,
        groupId: "",
        groupName: "",
        cultureInfoName: "",
        languageId: "",
        validFrom: "",
        validTo: "",
        name: "",
        identifier: "",
        sortProperty: "",
        sortOrder: "",
        skip: 0,
        take: Constants.paginationPageSize,
    })
    const [searchResult, setSearchResult] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 0,
        pages: [0],
        pageSize: Constants.paginationPageSize,
        maxPages: Constants.paginationMaxPages,
        totalItems: 0,
        totalPages: 0,
        startIndex: 0,
    });

    const [copiedTemplateText, setCopiedTemplateText] = useState();
    const dateNow = new Date();

    const [copyState, setCopyState] = useState(Constants.copyState.inactive);

    useEffect(() => {
        if (copyState === Constants.copyState.inactive) {
            return
        }

        const timeoutId = setTimeout(() => {
            setCopyState(Constants.copyState.inactive)
            setCopiedTemplateText(null)
        }, 2000)

        return () => clearTimeout(timeoutId)
    }, [copyState]);

    useEffect(() => {
        searchInputRef?.current?.focus();
    }, [searchClickedCount]);

    const handleCopy = async (args, text) => {
        setCopiedTemplateText(args.templateText);

        navigator.clipboard.writeText(text);
        setCopyState(Constants.copyState.active);

        if (args.selectedEditorInsertPoint) {
            const insertedEditorText = _.cloneDeep(args.selectedEditorInsertPoint.EditorTextTemplate);
            editorFileRef.current.DxmlMaxTextId++;
            insertedEditorText.DxmlContent = insertedEditorText.DxmlContent.replace(`Text_id="${insertedEditorText.Id}"`, `Text_id="${editorFileRef.current.DxmlMaxTextId}"`)
            insertedEditorText.Id = editorFileRef.current.DxmlMaxTextId.toString();

            if (args.templateText.ValueType === Constants.textLibraryValueType.plainText) {
                insertedEditorText.HtmlContent = insertedEditorText.HtmlContent.replace(args.selectedEditorInsertPoint.Def, text);
            } else {
                insertedEditorText.HtmlContent = text;
            }

            args.selectedEditorInsertPoint.Items.push(insertedEditorText);

            setSelectedEditorInsertPoint(null);

            autoSaveEditorFile();
        }

        let quill = lastUsedQuillRef?.current;
        if (quill) {
            let indexToUse = getAndCheckIndex(quill, lastUsedQuillIndexRef.current);
            quill?.focus();

            // quill?.clipboard.dangerouslyPasteHTML(indexToUse, `<br />${text}`);
            if (!insideEmptyParagraph(quill, indexToUse)) {
                if (atStartOfParagraph(quill, indexToUse)) {
                    quill.insertText(indexToUse, "\n");
                } else if (atEndOfParagraph(quill, indexToUse)) {
                    quill.insertText(indexToUse, "\n");
                    indexToUse++;
                } else {
                    quill.insertText(indexToUse, "\n\n");
                    indexToUse++;
                }
            }
            // When it inserts a paragraph into a text, that trailing \n is what makes Quill render a new empty paragraph after it.
            // Because Quill always ensures the document ends with a final newline (so the cursor has somewhere to go).
            // Thus it removes the last \n
            let insertedDelta = quill.clipboard.convert(text);
            if (insertedDelta.ops?.at(-1)?.insert === "\n") {
                insertedDelta.ops.pop();
            }
            const Delta = Quill.import("delta");
            quill.updateContents(new Delta().retain(indexToUse).concat(insertedDelta));

            // Because quill.updateContents() doesn't update selection/cursor, it needs to update selection
            const insertedLength = insertedDelta.length();
            quill.setSelection(indexToUse + insertedLength, 0);

            searchInputRef.current.focus();
            searchResultItemsRef.current[args.searchItemIndex]?.focus();
        }
    }

    const handleCopySelection = async (args) => {
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        const container = document.createElement('div');
        container.appendChild(range.cloneContents());
        handleCopy(args, container.innerHTML);
        setShowCopyText(null)
    }

    const handleTextClick = async (args) => {
        handleCopy(args, args.templateText.Values.find(x => x.LanguageId === args.searchRequest.languageId)?.Text);
    }

    const getTemplateTextFromLanguage = (templateText) => {
        var text = templateText.Values.find(x => x.LanguageId === searchRequest.languageId)?.Text;
        return { __html: text };
    }

    const loadAction = useToastAction();
    const searchAction = useToastAction();

    const [inputDisabled, setInputDisabled] = useState(false);

    useEffect(() => {
        setInputDisabled(searchAction.isExecuting || isLoading || editorDisabled);
    }, [searchAction.isExecuting, isLoading, editorDisabled])

    const handleLanguages = (languagesResult) => {
        let languages = languagesResult.map(x => { return { name: x.Name, value: x.Value, cultureInfoName: x.CultureInfoName } });
        languages.forEach(language => {
            if (language.name === 'Danish') {
                language.name = te(translations.edit.library.danish);
            } else if (language.name === 'English') {
                language.name = te(translations.edit.library.english);
            } else if (language.name === 'Finnish') {
                language.name = te(translations.edit.library.finnish);
            } else if (language.name === 'Norwegian') {
                language.name = te(translations.edit.library.norwegian);
            } else if (language.name === 'Swedish') {
                language.name = te(translations.edit.library.swedish);
            }
        });
        return languages;
    }

    const handleLanguageId = (languages) => {
        let languageId = languages[0].value;
        if (editorFileRef.current.CultureInfo) {
            var language = languages.find(x => x.cultureInfoName.toLowerCase() === editorFileRef.current.CultureInfo.toLowerCase());
            if (language) {
                languageId = language.value;
            }
        }
        else if (editorFileRef.current.WebEditorSettings?.LanguageCode) {
            var language = languages.find(x => x.cultureInfoName.toLowerCase().split("-")[0] === editorFileRef.current.WebEditorSettings.LanguageCode.toLowerCase());
            if (language) {
                languageId = language.value;
            }
        }

        let hasCultureInfoName = false;
        if (selectedEditorInsertPoint) {
            var language = languages.find(x => x.cultureInfoName.toLowerCase() === selectedEditorInsertPoint.CultureInfo.toLowerCase());
            if (language) {
                languageId = language.value;
                hasCultureInfoName = true;
            }
        }

        return { languageId, hasCultureInfoName };
    }

    const loadData = async (selectedEditorInsertPointChanged) => {
        loadAction.execute(async () => {
            const [groupsResult, languagesResult] = await Promise.all([
                getTextLibraryGroups(startParameter),
                getTextLibraryLanguages(),
            ]);

            const groups = groupsResult.map(x => { return { name: x.Name, value: x.Value } })
            const languages = handleLanguages(languagesResult);

            setLanguageOptions(languages);

            const { languageId, hasCultureInfoName } = handleLanguageId(languages);

            groups.unshift({ name: `<${te(translations.edit.library.all)}>`, value: "" })
            setGroupOptions(groups);

            let groupId = "";
            let hasGroupName = false;
            if (selectedEditorInsertPoint) {
                var group = groups.find(x => x.name === selectedEditorInsertPoint.Group);
                if (group) {
                    groupId = group.value;
                    hasGroupName = true;
                }
            }

            setSearchRequest({
                startParameter: startParameter,
                groupId: groupId,
                groupName: "",
                hasGroupName: hasGroupName,
                cultureInfoName: "",
                hasCultureInfoName: hasCultureInfoName,
                languageId: languageId,
                validFrom: "",
                validTo: "",
                name: "",
                identifier: "",
                sortProperty: sortPropertyOptions[0].value,
                sortOrder: sortDirectionOptions[0].value,
                skip: 0,
                take: Constants.paginationPageSize,
            });

            if (selectedEditorInsertPointChanged) {
                setSearchResult(null);
            }

        }, terr(translations.errors.failLoadData));
    }

    useEffect(() => {
        loadData(false)
    }, [startParameter]);

    useEffect(() => {
        if (selectedEditorInsertPoint?.Id) {
            loadData(true);
        }
    }, [selectedEditorInsertPoint?.Id]);

    useEffect(() => {
        // When the user selects another richtext editor (not insertPoint), it resets the language by cultureInfo from DXML
        if (lastUsedQuillRef.current && !selectedEditorInsertPoint && languageOptions?.length > 0) {
            const { languageId, hasCultureInfoName } = handleLanguageId(languageOptions);
            setSearchRequest(prevState => ({
                ...prevState,
                languageId: languageId,
                hasCultureInfoName: hasCultureInfoName
            }));
            if (languageId !== searchRequest?.languageId) {
                setSearchResult(null);
            }
        }
    }, [lastUsedQuillRef.current])

    let TextLibrarySearchSchema = yup.object().shape({
        language: yup.string().required(te(translations.edit.inputFieldValidation.required)),
    });

    const handlePaginationChange = async (newPagination) => {
        setPagination(newPagination);
        const newSearchRequest = _.cloneDeep(searchRequest);
        newSearchRequest.skip = newPagination.startIndex;
        setSearchRequest(newSearchRequest);

        await SearchText(newSearchRequest, newPagination);
    }

    const SearchText = async (newSearchRequest, newPagination) => {
        searchAction.execute(async () => {
            const res = await searchTextLibrary(newSearchRequest);

            setSearchResult(res);
            setActiveTemplateText(res.Texts.length > 1 ? null : res.Texts[0]);

            const clonedPagination = _.cloneDeep(newPagination ? newPagination : pagination);
            setPagination({
                ...clonedPagination,
                totalItems: res.Count,
                totalPages: Math.ceil(res.Count / clonedPagination.pageSize),
                currentPage: newSearchRequest.startIndex === 0 ? 0 : clonedPagination.currentPage,
            })
        }, terr(translations.errors.failSearch));
    }

    const handleSearch = async () => {
        const request = _.cloneDeep(searchRequest);
        request.skip = 0;
        request.take = Constants.paginationPageSize;
        setSearchRequest(request);
        await SearchText(request);
        searchResultItemsRef.current[0]?.focus();
    }

    const handleChange = ({ name, type, value, checked }) => {
        if (type === "checkbox") {
            setSearchRequest(prevState => ({
                ...prevState,
                [name]: checked,
            }));
        } else if (name === "name") {
            setSearchRequest(prevState => ({
                ...prevState,
                name: value,
                identifier: value
            }));
        } else {
            setSearchRequest(prevState => ({
                ...prevState,
                [name]: value
            }));
        }
    };

    return !loadAction.isExecuting && (
        <div className=" tw-h-full tw-w-full">
            <div >
                {/* <SidebarSelect
                    name="groupId"
                    value={searchRequest.groupId}
                    label={te(translations.edit.library.group)}
                    disabled={searchRequest.hasGroupName || inputDisabled}
                    onChange={(e) => handleChange(e.target)}
                    options={groupOptions}
                />
                
                <SidebarDate
                    name="validFrom"
                    value={searchRequest.validFrom}
                    disabled={inputDisabled}
                    label={te(translations.edit.library.validFrom)}
                    onChange={(e) => handleChange(e.target)}
                />
                <SidebarDate
                    name="validTo"
                    value={searchRequest.validTo}
                    disabled={inputDisabled}
                    label={te(translations.edit.library.validTo)}
                    onChange={(e) => handleChange(e.target)}
                /> */}
                <div className="tw-relative tw-mt-2 tw-rounded-md tw-shadow-sm tw-mx-6">
                    <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3">
                        <MagnifyingGlassIcon className="tw-h-5 tw-w-5 tw-text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                        name="name"
                        autoFocus
                        ref={searchInputRef}
                        autoComplete="off"
                        value={searchRequest.name}
                        disabled={inputDisabled}
                        onChange={(e) => handleChange(e.target)}
                        placeholder={te(translations.edit.library.name)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        className="tw-block tw-w-full tw-rounded-md tw-ring-1 tw-ring-inset tw-ring-gray-300 tw-border-0 tw-py-1.5 tw-pl-10 tw-text-gray-900 placeholder:tw-text-gray-400 sm:tw-text-sm sm:tw-leading-6"
                    />
                </div>
                {/* <SidebarTextInput
                    name="name"
                    value={searchRequest.name}
                    disabled={inputDisabled}
                    label={te(translations.edit.library.name)}
                    onChange={(e) => handleChange(e.target)}
                /> */}
                <SidebarSelect
                    name="languageId"
                    value={searchRequest.languageId}
                    label={te(translations.edit.library.language)}
                    disabled={searchRequest.hasCultureInfoName || inputDisabled}
                    onChange={(e) => handleChange(e.target)}
                    options={languageOptions}
                />
                <SidebarSelect
                    name="groupId"
                    value={searchRequest.groupId}
                    label={te(translations.edit.library.group)}
                    onChange={(e) => handleChange(e.target)}
                    options={groupOptions}
                />
                {/* <SidebarSelect
                    name="sortProperty"
                    value={searchRequest.sortProperty}
                    disabled={inputDisabled}
                    label={te(translations.edit.library.sortby)}
                    onChange={(e) => {
                        const clonedSearchRequest = _.cloneDeep(searchRequest);
                        clonedSearchRequest.sortProperty = e.target.value;
                        setSearchRequest(clonedSearchRequest);
                    }}
                    options={sortPropertyOptions}
                />
                <SidebarSelect
                    name="sortDirection"
                    value={searchRequest.sortOrder}
                    disabled={inputDisabled}
                    onChange={(e) => {
                        const clonedSearchRequest = _.cloneDeep(searchRequest);
                        clonedSearchRequest.sortOrder = e.target.value;
                        setSearchRequest(clonedSearchRequest);
                    }}
                    options={sortDirectionOptions}
                /> */}

                <div className="tw-flex tw-justify-end tw-mt-6 tw-px-4 sm:tw-px-6">
                    <button
                        type="button"
                        disabled={inputDisabled}
                        onClick={handleSearch}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSearch();
                            }
                        }}
                        className="tw-w-48 tw-justify-center tw-inline-flex tw-items-center tw-px-3 tw-py-2 tw-border tw-border-transparent tw-text-sm tw-leading-4 tw-font-medium tw-rounded-md tw-shadow-sm tw-text-white tw-bg-indigo-700 hover:tw-bg-indigo-700-hover disabled:tw-opacity-50 focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-indigo-700"
                    >
                        {
                            searchAction.isExecuting && (
                                <svg className="tw-animate-spin tw--ml-1 tw-mr-3 tw-h-4 tw-w-4 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )
                        }
                        {te(translations.edit.search)}
                    </button>
                </div>
            </div>

            <div className="tw-px-4 sm:tw-px-6 tw-mt-16 tw-flex tw-flex-col">
                {searchResult && (
                    <div className="tw-text-sm tw-font-medium tw-text-indigo-700 tw-mt-3 tw-pb-2">
                        {searchResult.Count === 0
                            ?
                            `0 ${te(translations.edit.library.texts)}`
                            :
                            `${searchResult.Count} ${te(translations.edit.library.texts)}, ${te(translations.edit.library.page)} ${pagination.currentPage + 1} ${te(translations.edit.library.of)} ${pagination.totalPages}`}
                    </div>
                )}

                <div className="tw-space-y-2">
                    {
                        searchResult && searchResult.Texts.map((templateText, index) => (
                            <button
                                key={templateText.Id}
                                type="button"
                                ref={el => searchResultItemsRef.current[index] = el}
                                autoFocus={index === 0}
                                className="tw-relative tw-px-3 tw-py-2 tw-cursor-default tw-block tw-w-full tw-shadow-sm tw-text-left tw-text-sm tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded-md disabled:tw-opacity-50 focus:tw-border-indigo-700"
                                disabled={inputDisabled}
                                onKeyDown={(e) => {
                                    /*
                                        Only handle key events if the active element is a button.
                                        That makes it possible to use the arrow keys to scroll the text for long texts.
                                    */
                                    if (document.activeElement.tagName === "BUTTON") {
                                        if (e.key === "ArrowRight") {
                                            e.preventDefault();
                                            setActiveTemplateText(searchResult.Texts[index]);
                                        }
                                        else if (e.key === "ArrowLeft") {
                                            e.preventDefault();
                                            setActiveTemplateText(null);
                                        }
                                        else if (e.key === "ArrowUp") {
                                            e.preventDefault();
                                            if (index > 0) {
                                                searchResultItemsRef.current[index - 1].focus();
                                                if (activeTemplateText)
                                                    setActiveTemplateText(searchResult.Texts[index - 1]);
                                            }
                                        }
                                        else if (e.key === "ArrowDown") {
                                            e.preventDefault();
                                            if (index < searchResult.Texts.length - 1)
                                                searchResultItemsRef.current[index + 1].focus();
                                            if (activeTemplateText)
                                                setActiveTemplateText(searchResult.Texts[index + 1]);
                                        }
                                        else if (e.key === "Enter") {
                                            e.preventDefault();
                                            if (e.shiftKey) {
                                                setShowCopyText({
                                                    templateText: templateText,
                                                    searchRequest: searchRequest,
                                                    selectedEditorInsertPoint: selectedEditorInsertPoint,
                                                    searchItemIndex: index
                                                });
                                                setTimeout(() => {
                                                    // copyTextRef?.current?.focus(); // This doesn't work as expected, so we use document.getElementById instead
                                                    document.getElementById('quilltextLibraryComponent').firstChild.focus()
                                                }, 200);
                                            }
                                            else {
                                                handleTextClick({
                                                    templateText: templateText,
                                                    searchRequest: searchRequest,
                                                    selectedEditorInsertPoint: selectedEditorInsertPoint,
                                                    searchItemIndex: index
                                                })
                                            }
                                        }
                                    }
                                }}
                                onClick={(e) => {
                                    setActiveTemplateText(activeTemplateText === templateText ? null : templateText);
                                }}
                            >
                                <span className="tw-sr-only">{te(translations.edit.library.copyText)}</span>
                                {
                                    copiedTemplateText && copiedTemplateText.Id === templateText.Id && (
                                        <Transition
                                            enter="tw-transform tw-ease-out tw-duration-200 tw-transition tw-origin-bottom"
                                            enterFrom="tw-scale-95 tw-translate-y-0.5 tw-opacity-0"
                                            enterTo="tw-scale-100 tw-translate-y-0 tw-opacity-100"
                                            leave="tw-transition tw-ease-in tw-duration-100"
                                            leaveFrom="tw-opacity-100"
                                            leaveTo="tw-opacity-0"
                                        >
                                            <span
                                                xshow="true"
                                                className="tw-absolute tw-inset-x-0 tw-bottom-full tw-mb-2.5 tw-flex tw-justify-center"
                                            >
                                                <span className="tw-bg-indigo-700 tw-text-white tw-rounded-md text-[0.625rem] tw-leading-4 tw-tracking-wide tw-font-semibold tw-uppercase tw-py-1 tw-px-12 tw-filter tw-drop-shadow-md">
                                                    <svg width="16" height="6" viewBox="0 0 16 6" className="tw-text-indigo-700 tw-absolute tw-top-full tw-left-1/2 tw--mt-px tw--ml-2" >
                                                        <path fillRule="evenodd" clipRule="evenodd" d="M15 0H1V1.00366V1.00366V1.00371H1.01672C2.72058 1.0147 4.24225 2.74704 5.42685 4.72928C6.42941 6.40691 9.57154 6.4069 10.5741 4.72926C11.7587 2.74703 13.2803 1.0147 14.9841 1.00371H15V0Z" fill="currentColor"></path>
                                                    </svg>
                                                    {(copyState === Constants.copyState.expired)
                                                        ?
                                                        te(translations.edit.library.textHasExpired)
                                                        :
                                                        te(translations.edit.library.copied)
                                                    }
                                                </span>
                                            </span>
                                        </Transition>

                                    )}
                                <div className="tw-flex tw-justify-between">
                                    <span className="tw-block tw-truncate tw-pr-2">
                                        {templateText.Identifier} - {templateText.Name}
                                    </span>
                                    <div className="tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-cursor-pointer">
                                        <span
                                            onClick={() => {
                                                setShowCopyText({
                                                    templateText: templateText,
                                                    searchRequest: searchRequest,
                                                    selectedEditorInsertPoint: selectedEditorInsertPoint,
                                                });
                                                setTimeout(() => {
                                                    // copyTextRef?.current?.focus(); // This doesn't work as expected, so we use document.getElementById instead
                                                    document.getElementById('quilltextLibraryComponent')?.firstChild?.focus()
                                                }, 200);
                                            }}
                                            title={te(translations.edit.library.copyPartialTextToClipboard)}
                                            aria-label={te(translations.edit.library.copyPartialTextToClipboard)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" viewBox="-120 -120 752 752" stroke="currentColor">
                                                <g>
                                                    <path d="M256,438.9v-36.6c-37.4,0.5-86.6-21-123.6-52.4c-18.5-15.5-34.1-33.2-44.3-50.1c-10.4-16.9-15-32.6-14.9-43.8
                                                    c-0.1-11.2,4.5-26.9,14.9-43.8c15.3-25.4,42.8-52.3,74-71.5c31.2-19.5,65.9-31.1,94-30.9c37.4-0.5,86.6,21,123.6,52.4
                                                    c18.5,15.5,34.1,33.2,44.3,50.1c10.4,16.9,15,32.6,14.9,43.8c0.1,11.2-4.5,26.9-14.9,43.8c-15.3,25.4-42.8,52.3-74,71.5
                                                    c-31.2,19.5-65.9,31.1-94,30.9V438.9v36.6c62.3-0.5,122.8-29.8,170.6-69.5c23.8-20,44.5-43,59.8-68c15.2-25,25.5-52.5,25.6-81.9
                                                    c-0.1-29.4-10.4-56.9-25.6-81.9c-23-37.5-57.8-70.6-97.8-95.6C348.4,53.7,302.8,36.7,256,36.6C193.7,37,133.2,66.4,85.4,106.1
                                                    c-23.8,20-44.5,43-59.8,68C10.4,199.1,0.1,226.6,0,256c0.1,29.4,10.4,56.9,25.6,81.9c23,37.5,57.8,70.6,97.8,95.6
                                                    c40.1,24.8,85.8,41.8,132.6,41.9V438.9z M219.4,256h-36.6c0,40.4,32.7,73.1,73.1,73.1c40.4,0,73.1-32.7,73.1-73.1
                                                    c0-40.4-32.7-73.1-73.1-73.1c-40.4,0-73.1,32.7-73.1,73.1H219.4H256v0v0l0,0H219.4z"/>
                                                </g>
                                            </svg>
                                        </span>
                                        <span
                                            onClick={() => handleTextClick({
                                                templateText: templateText,
                                                searchRequest: searchRequest,
                                                selectedEditorInsertPoint: selectedEditorInsertPoint,
                                            })}
                                            title={te(translations.edit.library.copyFullTextToClipboard)}
                                            aria-label={te(translations.edit.library.copyFullTextToClipboard)}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                                {
                                    activeTemplateText && activeTemplateText.Id === templateText.Id &&
                                    <div className="tw-mt-2 tw-overflow-y-auto tw-max-h-24" dangerouslySetInnerHTML={getTemplateTextFromLanguage(templateText)}>
                                    </div>
                                }
                            </button>
                        ))
                    }
                </div>
                {searchResult && searchResult.Count > 0 && (
                    <div className="tw-pb-3">
                        <Pagination
                            pagination={pagination}
                            disabled={inputDisabled}
                            onPaginationChange={(e) => handlePaginationChange(e)}
                            showPageIndex={false}
                        />
                    </div>
                )}
            </div>
            <GenericModal
                open={showCopyText !== null}
                className="copy-text-modal"
                setOpen={setShowCopyText}
                title={showCopyText?.templateText?.Name}
                confirmButtonText={"Copy"}
                showCancelButton={true}
                cancelButtonText={"Cancel"}
                onConfirm={() => handleCopySelection(showCopyText)}
                onCancel={() => setShowCopyText(null)}
            >
                {showCopyText?.templateText && (
                    <RichTextEditor
                        ref={copyTextRef}
                        editorText={{
                            Id: "textLibraryComponent",
                            Type: "Text",
                            WriteEnabled: true,
                            ReactCssStyle: {
                                "marginTop": "2.83pt",
                                "marginBottom": "2.83pt",
                                "maxHeight": "15em",
                                "overflowY": "auto",
                            },
                            HtmlContent: getTemplateTextFromLanguage(showCopyText?.templateText).__html
                        }}
                    ></RichTextEditor>
                )}
            </GenericModal>
        </div>

    )
}